/**
 * React Hooks for Tech Request Operations
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  QuerySnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  TechRequest,
  TechRequestStatus,
  TechRequestPriority,
} from '../types/theo-workflow.types';
import * as techRequestService from '../services/tech-request.service';
import useTenantId, { tenantIdReadUnion } from './useTenantId';

/**
 * Hook to get tech requests for a specific deal with real-time updates
 */
export function useTechRequestsByDeal(dealId: string | null) {
  const [requests, setRequests] = useState<TechRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const tenantId = useTenantId();

  useEffect(() => {
    if (!dealId) {
      setRequests([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'tech_requests'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('dealId', '==', dealId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const requestsData = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            }) as TechRequest
        );
        setRequests(requestsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching tech requests:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [dealId, tenantId]);

  return { requests, loading, error };
}

/**
 * Hook to get tech requests by status with real-time updates
 */
export function useTechRequestsByStatus(status: TechRequestStatus) {
  const [requests, setRequests] = useState<TechRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const tenantId = useTenantId();

  useEffect(() => {
    const q = query(
      collection(db, 'tech_requests'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const requestsData = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            }) as TechRequest
        );
        setRequests(requestsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching tech requests by status:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [status, tenantId]);

  return { requests, loading, error };
}

/**
 * Hook to get all open tech requests, ordered by priority
 */
export function useOpenTechRequests() {
  const [requests, setRequests] = useState<TechRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const tenantId = useTenantId();

  useEffect(() => {
    // Fetch open requests and sort by priority
    techRequestService
      .getOpenTechRequests()
      .then((data) => {
        setRequests(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching open tech requests:', err);
        setError(err);
        setLoading(false);
      });

    // Set up real-time listener for open requests
    const q = query(
      collection(db, 'tech_requests'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('status', '==', TechRequestStatus.OPEN),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
      const requestsData = snapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as TechRequest
      );

      // Sort by priority
      const priorityOrder = {
        [TechRequestPriority.HIGH]: 0,
        [TechRequestPriority.MEDIUM]: 1,
        [TechRequestPriority.LOW]: 2,
      };

      const sorted = requestsData.sort(
        (a, b) => priorityOrder[a.priority as keyof typeof priorityOrder] - priorityOrder[b.priority as keyof typeof priorityOrder]
      );

      setRequests(sorted);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [tenantId]);

  return { requests, loading, error };
}

/**
 * Hook to create, update, and delete tech requests
 */
export function useTechRequestMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createTechRequest = async (
    data: Omit<TechRequest, 'id' | 'createdAt' | 'updatedAt' | 'status'>,
    userId?: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      const id = await techRequestService.createTechRequest(data, userId);
      setLoading(false);
      return id;
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const updateTechRequest = async (
    requestId: string,
    data: Partial<Omit<TechRequest, 'id' | 'createdAt' | 'createdBy' | 'dealId'>>
  ) => {
    setLoading(true);
    setError(null);
    try {
      await techRequestService.updateTechRequest(requestId, data);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const updateTechRequestStatus = async (
    requestId: string,
    status: TechRequestStatus
  ) => {
    setLoading(true);
    setError(null);
    try {
      await techRequestService.updateTechRequestStatus(requestId, status);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const resolveTechRequest = async (
    requestId: string,
    answer: string,
    userId?: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      await techRequestService.resolveTechRequest(requestId, answer, userId);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const updateTechRequestPriority = async (
    requestId: string,
    priority: TechRequestPriority
  ) => {
    setLoading(true);
    setError(null);
    try {
      await techRequestService.updateTechRequestPriority(requestId, priority);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const deleteTechRequest = async (requestId: string) => {
    setLoading(true);
    setError(null);
    try {
      await techRequestService.deleteTechRequest(requestId);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  return {
    createTechRequest,
    updateTechRequest,
    updateTechRequestStatus,
    resolveTechRequest,
    updateTechRequestPriority,
    deleteTechRequest,
    loading,
    error,
  };
}
